import { useState, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import styles from './FeedbackDrawer.module.css';

const FEATURES = [
  'Dashboard', 'ZILM Chat', 'Funding Intelligence', 'Compliance Tracker',
  'Analytics & Tools', 'Guided Workflows', 'Assessments', 'Intelligence Briefs',
  'Admin Panel', 'Tools & Calculators', 'AI Agents', "What's New",
  'Sign Up / Onboarding', 'Map Visualization', 'Overall Experience',
];

const DIMENSIONS = ['Usefulness', 'Accuracy', 'Speed', 'Clarity'] as const;

// Keep this list in sync with ALLOWED_ATTACHMENT_MIME in the gateway Worker.
const ACCEPT_TYPES =
  '.html,.htm,.png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,' +
  'text/html,image/png,image/jpeg,image/gif,image/webp,application/pdf,' +
  'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB — must match the Worker
const MAX_FILES = 10;

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mime: string, name: string): string {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) return '📄';
  if (mime.includes('word') || /\.docx?$/.test(name.toLowerCase())) return '📝';
  if (mime === 'text/html' || /\.html?$/.test(name.toLowerCase())) return '🌐';
  return '📎';
}

export function FeedbackDrawer() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState('');
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [nps, setNps] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit =
    !!feature &&
    DIMENSIONS.every((d) => (ratings[d] ?? 0) > 0) &&
    nps !== null;

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      setError('');
      const next: File[] = [...files];
      for (const f of Array.from(incoming)) {
        if (next.length >= MAX_FILES) {
          setError(`You can attach at most ${MAX_FILES} files.`);
          break;
        }
        if (f.size > MAX_FILE_BYTES) {
          setError(`'${f.name}' is larger than ${MAX_FILE_BYTES / (1024 * 1024)} MB.`);
          continue;
        }
        // De-duplicate by name+size (cheap signature, good enough).
        if (next.some((n) => n.name === f.name && n.size === f.size)) continue;
        next.push(f);
      }
      setFiles(next);
    },
    [files],
  );

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const gateway = import.meta.env.VITE_API_URL || 'https://api-gateway.dasa-c17.workers.dev';
      const token = useAuthStore.getState().token;

      const payload = {
        feature_id: feature,
        ratings: {
          usefulness: ratings.Usefulness ?? 0,
          accuracy: ratings.Accuracy ?? 0,
          speed: ratings.Speed ?? 0,
          clarity: ratings.Clarity ?? 0,
        },
        nps_score: nps,
        qualitative_text: text || undefined,
        url_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      };

      // If there are files, upload as multipart so the Worker can stream
      // them to R2. Otherwise keep the cheap JSON path.
      let res: Response;
      if (files.length > 0) {
        const form = new FormData();
        form.append('payload', JSON.stringify(payload));
        for (const f of files) form.append('file', f, f.name);
        res = await fetch(`${gateway}/api/v2/feedback`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: form,
        });
      } else {
        res = await fetch(`${gateway}/api/v2/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error?.message || 'Could not send feedback. Please try again.');
        return;
      }
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFeature('');
        setRatings({});
        setNps(null);
        setText('');
        setFiles([]);
        setOpen(false);
      }, 2000);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const setRating = (dim: string, val: number) => {
    setRatings(prev => ({ ...prev, [dim]: val }));
  };

  return (
    <>
      {/* Toggle button — fixed right edge */}
      <button
        className={styles.toggle}
        onClick={() => setOpen(!open)}
        aria-label="Open feedback"
      >
        Feedback
      </button>

      {/* Drawer */}
      <div className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.drawerTitle}>How can we improve?</h3>
            <p className={styles.drawerSub}>Your feedback shapes IntelliDash</p>
          </div>
          <button className={styles.close} onClick={() => setOpen(false)} aria-label="Close feedback">×</button>
        </div>

        {submitted ? (
          <div className={styles.success}>
            <span style={{ fontSize: 32 }}>✓</span>
            <p>Thank you for your feedback!</p>
          </div>
        ) : (
          <div className={styles.body}>
            {/* Feature selector */}
            <div className={styles.field}>
              <label className={styles.label}>Which feature?</label>
              <select className={styles.select} value={feature} onChange={e => setFeature(e.target.value)}>
                <option value="">Select a feature</option>
                {FEATURES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>

            {/* Star ratings per dimension */}
            {DIMENSIONS.map(dim => (
              <div key={dim} className={styles.field}>
                <label className={styles.label}>{dim}</label>
                <div className={styles.stars}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`${styles.star} ${(ratings[dim] ?? 0) >= star ? styles.starFilled : ''}`}
                      onClick={() => setRating(dim, star)}
                      aria-label={`${star} stars for ${dim}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* NPS */}
            <div className={styles.field}>
              <label className={styles.label}>How likely to recommend? (0-10)</label>
              <div className={styles.npsRow}>
                {Array.from({ length: 11 }, (_, i) => (
                  <button
                    key={i}
                    className={`${styles.npsBtn} ${nps === i ? styles.npsBtnActive : ''}`}
                    onClick={() => setNps(i)}
                  >
                    {i}
                  </button>
                ))}
              </div>
              <div className={styles.npsLabels}>
                <span>Not likely</span>
                <span>Very likely</span>
              </div>
            </div>

            {/* Free text */}
            <div className={styles.field}>
              <label className={styles.label}>Anything else?</label>
              <textarea
                className={styles.textarea}
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Tell us what's on your mind..."
                maxLength={2000}
                rows={3}
              />
            </div>

            {/* Attachments */}
            <div className={styles.field}>
              <label className={styles.label}>Attachments (optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_TYPES}
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  // Reset so the same file can be re-picked after removal
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              />
              <button
                type="button"
                className={styles.attachBtn}
                onClick={() => fileInputRef.current?.click()}
              >
                + Add files (HTML, PNG, PDF, Word)
              </button>
              <p className={styles.attachHint}>
                Up to {MAX_FILES} files, 20&nbsp;MB each.
              </p>

              {files.length > 0 && (
                <ul className={styles.attachList}>
                  {files.map((f, i) => (
                    <li key={`${f.name}-${f.size}-${i}`} className={styles.attachItem}>
                      <span className={styles.attachIcon} aria-hidden="true">
                        {fileIcon(f.type, f.name)}
                      </span>
                      <span className={styles.attachName} title={f.name}>
                        {f.name}
                      </span>
                      <span className={styles.attachSize}>{formatBytes(f.size)}</span>
                      <button
                        type="button"
                        className={styles.attachRemove}
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${f.name}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <p style={{ color: 'var(--red)', fontSize: 12, margin: '8px 0 0' }} role="alert">
                {error}
              </p>
            )}

            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Sending…' : 'Submit feedback'}
            </button>
          </div>
        )}
      </div>

      {/* Overlay */}
      {open && <div className={styles.overlay} onClick={() => setOpen(false)} />}
    </>
  );
}
